import { useState, useRef, useEffect, useCallback } from 'react'
import { useChatStore } from '../stores/chatStore'
import { useToastStore } from '../stores/toastStore'
import { track } from '../lib/analytics'
import ConfirmDialog from './ConfirmDialog'

const CONTENT_ITEM_WARNING_THRESHOLD = 4

function getContentItemCount(): number {
  const entries = useChatStore.getState().entries
  return entries.filter((e) => e.content_item != null).length
}

interface Props {
  open: boolean
  onClose: () => void
  threadId: number
  onImageCapture: (file: File) => void
  onLiveCamera: (stream: MediaStream) => void
}

type ExpandedOption = 'youtube' | 'article' | null

function getCsrfToken(): string {
  const match = document.cookie.match(/csrftoken=([^;]+)/)
  return match ? match[1] : ''
}

async function uploadFile(threadId: number, endpoint: string, file: File): Promise<void> {
  const formData = new FormData()
  formData.append('file', file)
  const res = await fetch(`/api/v1/research/threads/${threadId}/ingest/${endpoint}/`, {
    method: 'POST',
    headers: { 'X-CSRFToken': getCsrfToken() },
    credentials: 'same-origin',
    body: formData,
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error((body as Record<string, string>).detail || `Upload failed (${res.status})`)
  }
}

async function postUrl(threadId: number, endpoint: string, url: string): Promise<void> {
  const res = await fetch(`/api/v1/research/threads/${threadId}/ingest/${endpoint}/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': getCsrfToken(),
    },
    credentials: 'same-origin',
    body: JSON.stringify({ url }),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error((body as Record<string, string>).detail || `Import failed (${res.status})`)
  }
}

export default function ContentMenu({ open, onClose, threadId, onImageCapture, onLiveCamera }: Props) {
  const [expanded, setExpanded] = useState<ExpandedOption>(null)
  const [urlValue, setUrlValue] = useState('')
  const [importing, setImporting] = useState(false)
  const [visible, setVisible] = useState(false)
  const [animateIn, setAnimateIn] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const pendingActionRef = useRef<(() => Promise<void>) | null>(null)
  const { fetchTimeline } = useChatStore()
  const { addToast } = useToastStore.getState()

  const cameraRef = useRef<HTMLInputElement>(null)
  const galleryRef = useRef<HTMLInputElement>(null)
  const documentRef = useRef<HTMLInputElement>(null)
  const urlInputRef = useRef<HTMLInputElement>(null)

  // Handle open/close animation
  useEffect(() => {
    if (open) {
      setVisible(true)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimateIn(true))
      })
    } else {
      setAnimateIn(false)
      const timer = setTimeout(() => {
        setVisible(false)
        setExpanded(null)
        setUrlValue('')
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [open])

  // Focus URL input when expanded
  useEffect(() => {
    if (expanded) {
      setTimeout(() => urlInputRef.current?.focus(), 100)
    }
  }, [expanded])

  const guardImport = useCallback((action: () => Promise<void>) => {
    if (getContentItemCount() >= CONTENT_ITEM_WARNING_THRESHOLD) {
      pendingActionRef.current = action
      setConfirmOpen(true)
    } else {
      action()
    }
  }, [])

  const handleConfirm = () => {
    setConfirmOpen(false)
    pendingActionRef.current?.()
    pendingActionRef.current = null
  }

  const handleCancelConfirm = () => {
    setConfirmOpen(false)
    pendingActionRef.current = null
  }

  if (!visible) return null

  const handleClose = () => {
    setExpanded(null)
    setUrlValue('')
    onClose()
  }

  const doUrlImport = async (type: 'youtube' | 'blog', url: string) => {
    setImporting(true)
    track('content_import_started', { thread_id: threadId, content_type: type })
    try {
      await postUrl(threadId, type, url)
      track('content_import_completed', { thread_id: threadId, content_type: type })
      addToast('Import started', 'success')
      fetchTimeline(threadId)
      handleClose()
    } catch (err) {
      track('content_import_failed', { thread_id: threadId, content_type: type, error: err instanceof Error ? err.message : 'unknown' })
      addToast(err instanceof Error ? err.message : 'Import failed', 'error')
    } finally {
      setImporting(false)
    }
  }

  const handleUrlImport = (type: 'youtube' | 'blog') => {
    const trimmed = urlValue.trim()
    if (!trimmed || importing) return
    guardImport(() => doUrlImport(type, trimmed))
  }

  const doFileUpload = async (file: File, endpoint: string, label: string) => {
    setImporting(true)
    track('content_import_started', { thread_id: threadId, content_type: endpoint })
    try {
      await uploadFile(threadId, endpoint, file)
      track('content_import_completed', { thread_id: threadId, content_type: endpoint })
      addToast(`${label} import started`, 'success')
      fetchTimeline(threadId)
      handleClose()
    } catch (err) {
      track('content_import_failed', { thread_id: threadId, content_type: endpoint, error: err instanceof Error ? err.message : 'unknown' })
      addToast(err instanceof Error ? err.message : 'Upload failed', 'error')
    } finally {
      setImporting(false)
    }
  }

  const handleFileUpload = (file: File, endpoint: string, label: string) => {
    guardImport(() => doFileUpload(file, endpoint, label))
  }

  const handleFileChange = (endpoint: string, label: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFileUpload(file, endpoint, label)
    e.target.value = ''
  }

  const handleImageFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      onImageCapture(file)
      handleClose()
    }
    e.target.value = ''
  }

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
    const routeMap: Record<string, { endpoint: string; label: string }> = {
      pdf: { endpoint: 'pdf', label: 'PDF' },
      docx: { endpoint: 'docx', label: 'Word document' },
      txt: { endpoint: 'text', label: 'Text file' },
      md: { endpoint: 'text', label: 'Markdown file' },
      rtf: { endpoint: 'text', label: 'RTF file' },
    }
    const route = routeMap[ext]
    if (!route) {
      addToast("This file type isn't supported. Try PDF, Word, or text files.", 'error')
      return
    }
    handleFileUpload(file, route.endpoint, route.label)
  }

  const handleUrlKeyDown = (e: React.KeyboardEvent, type: 'youtube' | 'blog') => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleUrlImport(type)
    }
  }

  const toggleExpanded = (option: ExpandedOption) => {
    if (expanded === option) {
      setExpanded(null)
      setUrlValue('')
    } else {
      setExpanded(option)
      setUrlValue('')
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-300 ${animateIn ? 'bg-black/60' : 'bg-black/0'}`}
        onClick={handleClose}
      />

      {/* Bottom sheet */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 bg-surface rounded-t-2xl border-t border-elevated pb-8 safe-area-bottom transition-transform duration-300 ease-out ${
          animateIn ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="w-10 h-1 bg-text-muted/30 rounded-full mx-auto mt-3 mb-2" />
        <h3 className="text-lg font-medium text-text-primary px-4 mb-3">Add Content</h3>

        {importing && (
          <div className="flex items-center gap-3 px-4 py-3 mb-2 mx-4 bg-elevated rounded-xl animate-pulse">
            <span className="w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin shrink-0" />
            <span className="text-sm text-text-secondary">Uploading...</span>
          </div>
        )}

        <div className={`space-y-1 px-4 max-h-[60vh] overflow-y-auto ${importing ? 'opacity-50 pointer-events-none' : ''}`}>
          {/* Import YouTube Video */}
          <div>
            <button
              onClick={() => toggleExpanded('youtube')}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-elevated/80 transition-colors"
            >
              <svg className="w-6 h-6 text-red-500 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
              <span className="text-text-primary font-medium">Import YouTube Video</span>
            </button>
            {expanded === 'youtube' && (
              <div className="flex gap-2 px-3 pb-3">
                <input
                  ref={urlInputRef}
                  type="url"
                  value={urlValue}
                  onChange={(e) => setUrlValue(e.target.value)}
                  onKeyDown={(e) => handleUrlKeyDown(e, 'youtube')}
                  placeholder="Paste YouTube URL..."
                  className="flex-1 bg-elevated text-text-primary placeholder-text-muted rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent"
                />
                <button
                  onClick={() => handleUrlImport('youtube')}
                  disabled={!urlValue.trim() || importing}
                  className="px-4 py-2 bg-accent text-bg rounded-lg text-sm font-medium hover:bg-accent-hover transition-colors disabled:opacity-30"
                >
                  {importing ? '...' : 'Import'}
                </button>
              </div>
            )}
          </div>

          {/* Import Web Article */}
          <div>
            <button
              onClick={() => toggleExpanded('article')}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-elevated/80 transition-colors"
            >
              <svg className="w-6 h-6 text-accent shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5a17.92 17.92 0 01-8.716-2.247m0 0A8.966 8.966 0 013 12c0-1.264.26-2.466.727-3.559" />
              </svg>
              <span className="text-text-primary font-medium">Import Web Article</span>
            </button>
            {expanded === 'article' && (
              <div className="flex gap-2 px-3 pb-3">
                <input
                  ref={urlInputRef}
                  type="url"
                  value={urlValue}
                  onChange={(e) => setUrlValue(e.target.value)}
                  onKeyDown={(e) => handleUrlKeyDown(e, 'blog')}
                  placeholder="Paste article URL..."
                  className="flex-1 bg-elevated text-text-primary placeholder-text-muted rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent"
                />
                <button
                  onClick={() => handleUrlImport('blog')}
                  disabled={!urlValue.trim() || importing}
                  className="px-4 py-2 bg-accent text-bg rounded-lg text-sm font-medium hover:bg-accent-hover transition-colors disabled:opacity-30"
                >
                  {importing ? '...' : 'Import'}
                </button>
              </div>
            )}
          </div>

          {/* Import Document (PDF, Word, Text, Markdown, RTF) */}
          <button
            onClick={() => documentRef.current?.click()}
            className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-elevated/80 transition-colors"
          >
            <svg className="w-6 h-6 text-accent shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
            <span className="text-text-primary font-medium">Import Document</span>
          </button>

          {/* Scan Notes */}
          <button
            onClick={async () => {
              track('notecard_scan_initiated', { thread_id: threadId })
              try {
                const stream = await navigator.mediaDevices.getUserMedia({
                  video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
                })
                onLiveCamera(stream)
                handleClose()
              } catch (err) {
                const msg = err instanceof Error ? err.message : String(err)
                addToast(`Camera: ${msg}`, 'error')
              }
            }}
            className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-elevated/80 transition-colors"
          >
            <svg className="w-6 h-6 text-accent shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
            </svg>
            <span className="text-text-primary font-medium">Scan Notes</span>
          </button>

          {/* Choose Photo */}
          <button
            onClick={() => galleryRef.current?.click()}
            className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-elevated/80 transition-colors"
          >
            <svg className="w-6 h-6 text-accent shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
            </svg>
            <span className="text-text-primary font-medium">Choose Photo</span>
          </button>
        </div>

        <button
          onClick={handleClose}
          className="w-full mt-2 px-4 py-3 text-text-secondary text-sm font-medium hover:text-text-primary transition-colors"
        >
          Cancel
        </button>

        {/* Hidden file inputs */}
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={handleImageFile} className="hidden" />
        <input ref={galleryRef} type="file" accept="image/jpeg,image/png,image/heic,image/heif,image/webp" onChange={handleImageFile} className="hidden" />
        <input ref={documentRef} type="file" accept=".pdf,.docx,.txt,.md,.rtf" onChange={handleDocumentChange} className="hidden" />
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Lots of imports"
        message="This thread already has several imported items. Adding more may shorten the AI's memory of your conversation. Consider starting a new thread for new topics."
        confirmLabel="Import Anyway"
        onConfirm={handleConfirm}
        onCancel={handleCancelConfirm}
      />
    </>
  )
}
